/**
 * useHomeARDemoCrmTourLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait HomeARDemoCrmTour
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, runTickFrame, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import { isFieldValue } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';
import type { EntityFieldWrite, EntityFrameState, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, HomeARDemoCrmScene, HomeARDemoCrmTourEventPayloadMap } from '@app/shared';

export interface HomeARDemoCrmTourConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  autoPlay: boolean;
  captions?: string[];
  compact: boolean;
  demo: string;
  demoTheme: string;
  desktopFrame: boolean;
  doneLabel?: string;
  events: string[];
  introCaption?: string;
  loop: boolean;
  ofWord?: string;
  pauseLabel?: string;
  pausedLabel?: string;
  playLabel?: string;
  playWhenVisible: boolean;
  resetLabel?: string;
  runningLabel?: string;
  speedLabel?: string;
  stepIntervalMs: number;
  stepLabel?: string;
  stepWord?: string;
  summary: string;
  title: string;
  tourContent: { children: Array<string | { animateChanges: boolean; children: { align: string; children: Array<{ color: string; name: string; size: string; type: string } | { content: Array<Array<number | string> | string | string[]>; type: string; variant: string }>; className: Array<Array<Array<number | string> | string | string[]> | string>; direction: string; gap: string; type: string }[]; type: string } | { animation: string; children: { animateChanges: boolean; children: Array<string | { children: Array<{ align: string; "aria-hidden": boolean; children: { className: string; type: string }[]; className: string; direction: string; gap: string; type: string } | { children: { children: string[]; className: string; type: string }[]; type: string }>; direction: string; gap: string; type: string } | { children: string[]; padding: string; type: string }>[]; className: string[]; "data-theme": string; type: string }[]; enterEvent: string; leaveEvent: string; revealEvent: string[]; threshold: number; trigger: string; type: string }>; direction: string; gap: string; type: string };
}

const DEFAULT_HOME_AR_DEMO_CRM_TOUR_CONFIG = {
  autoPlay: false,
  compact: true,
  demo: "@trait.HomeARDemoCrmShell",
  demoTheme: "corporate-light",
  desktopFrame: true,
  events: ["QUALIFY_NORTHWIND", "PROPOSE_NORTHWIND", "WIN_BLUEBIRD", "WIN_NORTHWIND"],
  loop: true,
  playWhenVisible: true,
  stepIntervalMs: 2600,
  summary: "",
  title: "",
  tourContent: { children: [{ animation: "none", children: [{ animateChanges: true, children: [["if", "@config.desktopFrame", { children: [{ align: "center", "aria-hidden": true, children: [{ className: "w-2.5 h-2.5 rounded-full bg-error", type: "box" }, { className: "w-2.5 h-2.5 rounded-full bg-warning", type: "box" }, { className: "w-2.5 h-2.5 rounded-full bg-success", type: "box" }], className: "px-3 py-2 bg-muted border-b border-border", direction: "horizontal", gap: "xs", type: "stack" }, { children: [{ children: ["@config.demo"], className: "w-[1280px] h-[800px] overflow-hidden bg-background", type: "box" }], type: "scaled-diagram" }], direction: "vertical", gap: "none", type: "stack" }, { children: ["@config.demo"], padding: "md", type: "card" }]], className: ["if", "@config.desktopFrame", "rounded-container overflow-hidden border border-border shadow-elevation-popover bg-background", "rounded-container"], "data-theme": "@config.demoTheme", type: "box" }], enterEvent: "TOUR_SHOWN", leaveEvent: "TOUR_HIDDEN", revealEvent: ["if", "@config.playWhenVisible", "TOUR_VISIBLE", ""], threshold: 0.4, trigger: "scroll", type: "animated-reveal" }, { animateChanges: true, children: [{ align: "start", children: [{ color: "primary", name: "message-square-text", size: "md", type: "icon" }, { content: ["if", ["<", "@entity.shown", 0], "@config.introCaption", ["array/nth", "@config.captions", "@entity.shown"]], type: "typography", variant: "body" }], className: ["if", ["and", ["<", "@entity.shown", 0], ["==", "@config.introCaption", ""]], "hidden", ""], direction: "horizontal", gap: "sm", type: "stack" }], type: "box" }, "@trait.HomeARHomeCrmDemoTourTransport"], direction: "vertical", gap: "md", type: "stack" },
} as HomeARDemoCrmTourConfig;

type HomeARDemoCrmTourSlot = 'main';
export interface HomeARDemoCrmTourLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<HomeARDemoCrmTourSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARDemoCrmScene>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeARDemoCrmTourEventPayloadMap]: (payload?: HomeARDemoCrmTourEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeARDemoCrmTourConfig;
}

export function useHomeARDemoCrmTourLogic(config?: Partial<HomeARDemoCrmTourConfig>): HomeARDemoCrmTourLogicReturn {
  const mergedConfig: HomeARDemoCrmTourConfig = { ...DEFAULT_HOME_AR_DEMO_CRM_TOUR_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'HomeARDemoCrmScene';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'cursor': 0, 'shown': -1, 'offscreenPaused': false, 'speed': 1 });
  const _sharedTickEmitsRef = useRef<Array<() => void>>([]);
  const _sharedWritersRef = useRef<ReadonlyArray<(scratch: EntityFrameState) => readonly EntityFieldWrite[]>>([
    // writer HomeARHomeCrmDemoTourTransport.transportTick
    (scratch: EntityFrameState): readonly EntityFieldWrite[] => {
      const writes: EntityFieldWrite[] = [];
      const frame: Record<string, EventPayloadValue | undefined> = scratch;
      const _emits = _sharedTickEmitsRef.current;
      if (((frame?.active === true) && ((frame?.running === true) && !(frame?.done === true)))) {
        writes.push({ field: 'elapsedMs', value: ((__v) => (isFieldValue(__v) ? __v : undefined))((+(frame?.elapsedMs ?? 0) + 32)) });
        if (((+(frame?.elapsedMs ?? 0) * +(frame?.speed ?? 0)) >= 2600)) {
          writes.push({ field: 'elapsedMs', value: ((__v) => (isFieldValue(__v) ? __v : undefined))(0) });
          writes.push({ field: 'steps', value: ((__v) => (isFieldValue(__v) ? __v : undefined))((+(frame?.steps ?? 0) + 1)) });
          _emits.push(((_p) => () => { eventBus.emit('LEARN_STEP', _p, { trait: 'HomeARHomeCrmDemoTourTransport' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARHomeCrmDemoTourTransport', { event: 'LEARN_STEP', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARHomeCrmDemoTourTransport' } }); eventBus.emit('UI:HomeAR.HomeARDemoPeopleTour.LEARN_STEP', _p, { trait: 'HomeARHomeCrmDemoTourTransport' }); eventBus.emit('UI:HomeAR.HomeARDemoStockPageTour.LEARN_STEP', _p, { trait: 'HomeARHomeCrmDemoTourTransport' }); eventBus.emit('UI:HomeAR.HomeARDemoHelpdeskPageTour.LEARN_STEP', _p, { trait: 'HomeARHomeCrmDemoTourTransport' }); eventBus.emit('UI:HomeAR.HomeARDemoInvoicePageTour.LEARN_STEP', _p, { trait: 'HomeARHomeCrmDemoTourTransport' }); eventBus.emit('UI:HomeAR.HomeARDemoComposeTour.LEARN_STEP', _p, { trait: 'HomeARHomeCrmDemoTourTransport' }); })({ steps: frame?.steps }));
        }
      }
      return writes;
    },
  ]);

  // Tick: HomeARDemoCrmSceneSharedTick (every 32ms)
  useEffect(() => {
    const interval = setInterval(() => {
      if (true) {
        runTickFrame(_sharedEntityId, _sharedWritersRef.current, _sharedStore);
        for (const _emit of _sharedTickEmitsRef.current.splice(0)) _emit();
      }
    }, 32);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<HomeARDemoCrmScene>;
  const data: Record<string, EntityRow[]> = { 'HomeARDemoCrmScene': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<HomeARDemoCrmTourSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeARDemoCrmScene'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        _fields['cursor'] = 0;
        fields = { ...(fields ?? {}), 'cursor': _fields['cursor'] } as Record<string, EventPayloadValue | undefined>;
        _fields['shown'] = -1;
        fields = { ...(fields ?? {}), 'shown': _fields['shown'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_STEP' && (((((fields?.active === true) && !(fields?.done === true)) && ((["QUALIFY_NORTHWIND", "PROPOSE_NORTHWIND", "WIN_BLUEBIRD", "WIN_NORTHWIND"]).length > 0)) && (+(fields?.cursor ?? 0) >= (["QUALIFY_NORTHWIND", "PROPOSE_NORTHWIND", "WIN_BLUEBIRD", "WIN_NORTHWIND"]).length)))) {
      // idle → idle (on LEARN_STEP)
        _fields['cursor'] = 0;
        fields = { ...(fields ?? {}), 'cursor': _fields['cursor'] } as Record<string, EventPayloadValue | undefined>;
        _fields['shown'] = -1;
        fields = { ...(fields ?? {}), 'shown': _fields['shown'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('TOUR_RESET', _p, { trait: 'HomeARDemoCrmTour' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoCrmTour', { event: 'TOUR_RESET', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoCrmTour' } }); })({  }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_STEP' && ((((fields?.active === true) && !(fields?.done === true)) && (+(fields?.cursor ?? 0) < (["QUALIFY_NORTHWIND", "PROPOSE_NORTHWIND", "WIN_BLUEBIRD", "WIN_NORTHWIND"]).length)))) {
      // idle → idle (on LEARN_STEP)
        {

        const count = (["QUALIFY_NORTHWIND", "PROPOSE_NORTHWIND", "WIN_BLUEBIRD", "WIN_NORTHWIND"]).length;

        const at = fields?.cursor;

        const next = (+(at ?? 0) + 1);

        _fields['shown'] = at;
        fields = { ...(fields ?? {}), 'shown': _fields['shown'] } as Record<string, EventPayloadValue | undefined>;
        _fields['cursor'] = next;
        fields = { ...(fields ?? {}), 'cursor': _fields['cursor'] } as Record<string, EventPayloadValue | undefined>;
        _fields['done'] = (!true && (+(next ?? 0) >= +(count ?? 0)));
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('TOUR_STEP', _p, { trait: 'HomeARDemoCrmTour' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoCrmTour', { event: 'TOUR_STEP', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoCrmTour' } }); })({ index: at, event: ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(["QUALIFY_NORTHWIND", "PROPOSE_NORTHWIND", "WIN_BLUEBIRD", "WIN_NORTHWIND"], Number(at ?? 0)) }));
        }

      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'TOUR_VISIBLE' && ((((fields?.active === true) && !(fields?.running === true)) && !(fields?.done === true)))) {
      // idle → idle (on TOUR_VISIBLE)
        _emits.push(((_p) => () => { eventBus.emit('TOUR_PLAY', _p, { trait: 'HomeARDemoCrmTour' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoCrmTour', { event: 'TOUR_PLAY', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoCrmTour' } }); })({  }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'TOUR_HIDDEN' && ((((fields?.active === true) && (fields?.running === true)) && !(fields?.done === true)))) {
      // idle → idle (on TOUR_HIDDEN)
        _fields['offscreenPaused'] = true;
        fields = { ...(fields ?? {}), 'offscreenPaused': _fields['offscreenPaused'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('TOUR_PLAY', _p, { trait: 'HomeARDemoCrmTour' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoCrmTour', { event: 'TOUR_PLAY', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoCrmTour' } }); })({  }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'TOUR_SHOWN' && (((fields?.offscreenPaused === true) && !(fields?.running === true)))) {
      // idle → idle (on TOUR_SHOWN)
        _fields['offscreenPaused'] = false;
        fields = { ...(fields ?? {}), 'offscreenPaused': _fields['offscreenPaused'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('TOUR_PLAY', _p, { trait: 'HomeARDemoCrmTour' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoCrmTour', { event: 'TOUR_PLAY', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoCrmTour' } }); })({  }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_RESET') {
      // idle → idle (on LEARN_RESET)
        _fields['cursor'] = 0;
        fields = { ...(fields ?? {}), 'cursor': _fields['cursor'] } as Record<string, EventPayloadValue | undefined>;
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['shown'] = -1;
        fields = { ...(fields ?? {}), 'shown': _fields['shown'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('TOUR_RESET', _p, { trait: 'HomeARDemoCrmTour' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoCrmTour', { event: 'TOUR_RESET', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoCrmTour' } }); })({  }));
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeARDemoCrmTourSlot[]; clear: HomeARDemoCrmTourSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeARDemoCrmTourSlot[], clear: [] as HomeARDemoCrmTourSlot[] };
          case 'LEARN_RESET': return { write: ['main'] as HomeARDemoCrmTourSlot[], clear: [] as HomeARDemoCrmTourSlot[] };
          case 'LEARN_STEP': return { write: ['main'] as HomeARDemoCrmTourSlot[], clear: [] as HomeARDemoCrmTourSlot[] };
          case 'TOUR_VISIBLE': return { write: ['main'] as HomeARDemoCrmTourSlot[], clear: [] as HomeARDemoCrmTourSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'HomeARDemoCrmScene': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'HomeARDemoCrmTour': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeARDemoCrmTourEventPayloadMap]: (payload?: HomeARDemoCrmTourEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'LEARN_STEP': (payload?: HomeARDemoCrmTourEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'TOUR_VISIBLE': () => dispatch('TOUR_VISIBLE'),
    'TOUR_HIDDEN': () => dispatch('TOUR_HIDDEN'),
    'TOUR_SHOWN': () => dispatch('TOUR_SHOWN'),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'TOUR_PLAY': () => dispatch('TOUR_PLAY'),
    'TOUR_STEP': (payload?: HomeARDemoCrmTourEventPayloadMap['TOUR_STEP']) => dispatch('TOUR_STEP', payload),
    'TOUR_RESET': () => dispatch('TOUR_RESET'),
  };

  useUIEvents(enqueueEvent, 'HomeAR.HomeARDemoCrmTour', ['INIT', 'LEARN_STEP', 'TOUR_VISIBLE', 'TOUR_HIDDEN', 'TOUR_SHOWN', 'LEARN_RESET'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'LEARN_STEP' (from HomeARHomeCrmDemoTourTransport) → trigger 'LEARN_STEP'
    const unsub_learn_step_from_homearhomecrmdemotourtransport_to_learn_step = eventBus.on('UI:HomeAR.HomeARHomeCrmDemoTourTransport.LEARN_STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoCrmTour', 'LEARN_STEP')) return;
      enqueueEvent('LEARN_STEP', event.payload);
    });
    // Listen for 'LEARN_RESET' (from HomeARHomeCrmDemoTourTransport) → trigger 'LEARN_RESET'
    const unsub_learn_reset_from_homearhomecrmdemotourtransport_to_learn_reset = eventBus.on('UI:HomeAR.HomeARHomeCrmDemoTourTransport.LEARN_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoCrmTour', 'LEARN_RESET')) return;
      enqueueEvent('LEARN_RESET', event.payload);
    });
    return () => {
      if (typeof unsub_learn_step_from_homearhomecrmdemotourtransport_to_learn_step === 'function') unsub_learn_step_from_homearhomecrmdemotourtransport_to_learn_step();
      if (typeof unsub_learn_reset_from_homearhomecrmdemotourtransport_to_learn_reset === 'function') unsub_learn_reset_from_homearhomecrmdemotourtransport_to_learn_reset();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  mergedConfig.captions = mergedConfig.captions ?? ([__t("std-almadar-demo-crm-page:tour.step1"), __t("std-almadar-demo-crm-page:tour.step2"), __t("std-almadar-demo-crm-page:tour.step3"), __t("std-almadar-demo-crm-page:tour.step4")]);
  mergedConfig.doneLabel = mergedConfig.doneLabel ?? (__t("std-almadar-demo-crm-page:tour.done"));
  mergedConfig.introCaption = mergedConfig.introCaption ?? (__t("std-almadar-demo-crm-page:tour.intro"));
  mergedConfig.ofWord = mergedConfig.ofWord ?? (__t("std-almadar-demo-crm-page:tour.ofWord"));
  mergedConfig.pauseLabel = mergedConfig.pauseLabel ?? (__t("std-almadar-demo-crm-page:tour.pause"));
  mergedConfig.pausedLabel = mergedConfig.pausedLabel ?? (__t("std-almadar-demo-crm-page:tour.paused"));
  mergedConfig.playLabel = mergedConfig.playLabel ?? (__t("std-almadar-demo-crm-page:tour.play"));
  mergedConfig.resetLabel = mergedConfig.resetLabel ?? (__t("std-almadar-demo-crm-page:tour.reset"));
  mergedConfig.runningLabel = mergedConfig.runningLabel ?? (__t("std-almadar-demo-crm-page:tour.running"));
  mergedConfig.speedLabel = mergedConfig.speedLabel ?? (__t("std-almadar-demo-crm-page:tour.speed"));
  mergedConfig.stepLabel = mergedConfig.stepLabel ?? (__t("std-almadar-demo-crm-page:tour.step"));
  mergedConfig.stepWord = mergedConfig.stepWord ?? (__t("std-almadar-demo-crm-page:tour.stepWord"));
  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
